import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchClaimRoleMembershipList } from 'common/services/api/ClaimRoleMembership/ClaimRoleMembership.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { ClaimRoleMembershipCreateContent } from './ClaimRoleMembershipCreateDrawerContent';
import { ClaimRoleMembershipDeleteDialogContent } from './ClaimRoleMembershipDeleteDialogContent';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './claimRoleMembershipSlice';

export function ClaimRoleMembershipTab() {
    const typeName = 'Role';

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.AppManager, Roles.UserManager ]);
    const shouldDisableDeleteButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableCreateEditButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);

    const [processing, setProcessing] = useState<boolean>(false);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters);

    const [sortModel, setSortModel] = usePalmSortModel('roleName');
    const { suiteId = '', tenantId = '', claimId = '' } = useParams();

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId, claimId },
        fetchClaimRoleMembershipList,
        setProcessing
    );

    const onGetRowLink = useCallback((claimRoleId: string) => {
        const [, roleId] = claimRoleId.split('.');
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Roles}/${roleId}/${Routes.Claims}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableButtons, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableCreateEditButton },
        { action: Operations.Delete, disabled: shouldDisableDeleteButton }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'roleId', columnName: 'Role ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'roleName', columnName: 'Role Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'claimValue', columnName: 'Claim Value', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        getRowLink={shouldDisableButtons ? undefined : onGetRowLink}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={ClaimRoleMembershipCreateContent}
        deleteFunction={ClaimRoleMembershipDeleteDialogContent}
        operationName={Operations.Link}
        isCreateButtonDisabled={shouldDisableCreateEditButton}
        filter={filterProp}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
